import React, { useState, useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import 'chartjs-adapter-date-fns';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale
);

interface MarketSentimentChartModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface ChartDataPoint {
  date: Date;
  fearGreed?: number;
  newsSentiment?: number;
}

const MarketSentimentChartModal: React.FC<MarketSentimentChartModalProps> = ({
  isOpen,
  onClose
}) => {
  const [chartData, setChartData] = useState<ChartDataPoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      fetchHistoricalData();
    }
  }, [isOpen]);

  const fetchHistoricalData = async () => {
    try {
      setLoading(true);
      setError(null);

      const { apiClient } = await import('@/services/api');
      
      // Calculate date range for 1 month
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - 30);

      // Fetch both Fear & Greed and News Sentiment historical data
      const [fearGreedRes, newsSentimentRes] = await Promise.allSettled([
        apiClient.get<any>('/market/fear-greed', { days: 30 }),
        apiClient.get<any>('/market/historical', {
          dataType: 'news_sentiment',
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString()
        })
      ]);

      // Process Fear & Greed data
      const fearGreedMap = new Map<string, number>();
      if (fearGreedRes.status === 'fulfilled') {
        const fearGreedData = (fearGreedRes.value as any).fearGreedIndex || (fearGreedRes.value as any).data?.fearGreedIndex;
        
        if (fearGreedData?.historical) {
          fearGreedData.historical.forEach((item: any) => {
            const dateKey = new Date(item.createdAt).toISOString().split('T')[0];
            fearGreedMap.set(dateKey, item.value);
          });
        }
      }

      // Process News Sentiment data
      const newsSentimentMap = new Map<string, { sum: number; count: number }>();
      if (newsSentimentRes.status === 'fulfilled') {
        const sentimentData = newsSentimentRes.value.data?.data || [];
        
        if (Array.isArray(sentimentData)) {
          sentimentData.forEach((item: any) => {
            const dateKey = new Date(item.created_at || item.createdAt || item.timestamp).toISOString().split('T')[0];
            // News sentiment is stored as value field (0-100 scale)
            const score = item.value || item.score || 0;
            
            // Accumulate all values for the day to calculate average
            if (newsSentimentMap.has(dateKey)) {
              const existing = newsSentimentMap.get(dateKey)!;
              newsSentimentMap.set(dateKey, {
                sum: existing.sum + score,
                count: existing.count + 1
              });
            } else {
              newsSentimentMap.set(dateKey, { sum: score, count: 1 });
            }
          });
        }
      }

      // Combine data by date
      const allDates = new Set([...fearGreedMap.keys(), ...newsSentimentMap.keys()]);
      const combinedData: ChartDataPoint[] = Array.from(allDates)
        .sort()
        .map(dateKey => {
          const sentimentData = newsSentimentMap.get(dateKey);
          return {
            date: new Date(dateKey),
            fearGreed: fearGreedMap.get(dateKey),
            newsSentiment: sentimentData ? sentimentData.sum / sentimentData.count : undefined
          };
        });

      setChartData(combinedData);

    } catch (err) {
      console.error('Error fetching historical market sentiment data:', err);
      setError('Failed to load historical data');
    } finally {
      setLoading(false);
    }
  };

  const getChartConfig = () => {
    return {
      labels: chartData.map(d => d.date),
      datasets: [
        {
          label: 'Fear & Greed Index',
          data: chartData.map(d => d.fearGreed),
          borderColor: '#4f46e5',
          backgroundColor: 'rgba(79, 70, 229, 0.1)',
          borderWidth: 2,
          pointRadius: 3,
          pointHoverRadius: 5,
          tension: 0.3,
          spanGaps: true
        },
        {
          label: 'News Sentiment',
          data: chartData.map(d => d.newsSentiment),
          borderColor: '#10b981',
          backgroundColor: 'rgba(16, 185, 129, 0.1)',
          borderWidth: 2,
          pointRadius: 3,
          pointHoverRadius: 5,
          tension: 0.3,
          spanGaps: true
        }
      ]
    };
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index' as const,
      intersect: false,
    },
    plugins: {
      legend: {
        position: 'top' as const,
        labels: {
          usePointStyle: true,
          padding: 15,
          font: {
            size: 12
          }
        }
      },
      title: {
        display: false
      },
      tooltip: {
        callbacks: {
          label: function(context: any) {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            if (context.parsed.y !== null) {
              label += Math.round(context.parsed.y);
            }
            return label;
          }
        }
      }
    },
    scales: {
      x: {
        type: 'time' as const,
        time: {
          unit: 'day' as const,
          displayFormats: {
            day: 'MMM d'
          }
        },
        title: {
          display: true,
          text: 'Date',
          font: {
            size: 12
          }
        },
        grid: {
          color: 'rgba(0, 0, 0, 0.05)'
        }
      },
      y: {
        min: 0,
        max: 100,
        title: {
          display: true,
          text: 'Score (0-100)',
          font: {
            size: 12
          }
        },
        grid: {
          color: 'rgba(0, 0, 0, 0.05)'
        },
        ticks: {
          callback: function(value: any) {
            return value;
          }
        }
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div 
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
          onClick={onClose}
        ></div>

        {/* Modal panel */}
        <div className="inline-block align-bottom card rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
          {/* Header */}
          <div className="px-6 py-4 border-b border-primary-custom">
            <div className="flex items-center justify-between">
              <h3 className="text-heading-3 font-semibold text-primary-custom">
                Market Sentiment Trends (30 Days)
              </h3>
              <button
                onClick={onClose}
                className="text-secondary-custom hover-text-primary-custom transition-colors"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="px-6 py-6">
            {loading ? (
              <div className="flex items-center justify-center h-96">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
              </div>
            ) : error ? (
              <div className="flex flex-col items-center justify-center h-96">
                <p className="text-error-custom text-body mb-4">{error}</p>
                <button
                  onClick={fetchHistoricalData}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Retry
                </button>
              </div>
            ) : chartData.length === 0 ? (
              <div className="flex items-center justify-center h-96">
                <p className="text-secondary-custom text-body">No historical data available</p>
              </div>
            ) : (
              <>
                <div className="h-96 mb-6">
                  <Line data={getChartConfig()} options={chartOptions} />
                </div>

                {/* Legend and info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="p-4 bg-secondary-custom rounded-lg">
                    <div className="flex items-center mb-2">
                      <div className="w-4 h-4 bg-indigo-600 rounded mr-2"></div>
                      <h4 className="text-body font-medium text-primary-custom">Fear & Greed Index</h4>
                    </div>
                    <p className="text-caption text-secondary-custom">
                      Market sentiment indicator ranging from extreme fear (0) to extreme greed (100).
                      Values below 25 indicate fear, above 75 indicate greed.
                    </p>
                  </div>

                  <div className="p-4 bg-secondary-custom rounded-lg">
                    <div className="flex items-center mb-2">
                      <div className="w-4 h-4 bg-green-500 rounded mr-2"></div>
                      <h4 className="text-body font-medium text-primary-custom">News Sentiment</h4>
                    </div>
                    <p className="text-caption text-secondary-custom">
                      Aggregated sentiment from news sources. Values below 40 are bearish, 
                      40-60 are neutral, and above 60 are bullish.
                    </p>
                  </div>
                </div>

                {/* Statistics */}
                <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center p-3 bg-secondary-custom rounded-lg">
                    <div className="text-caption text-secondary-custom mb-1">Data Points</div>
                    <div className="text-heading-3 font-semibold text-primary-custom">{chartData.length}</div>
                  </div>
                  <div className="text-center p-3 bg-secondary-custom rounded-lg">
                    <div className="text-caption text-secondary-custom mb-1">Avg F&G</div>
                    <div className="text-heading-3 font-semibold text-primary-custom">
                      {chartData.filter(d => d.fearGreed).length > 0
                        ? Math.round(
                            chartData.reduce((sum, d) => sum + (d.fearGreed || 0), 0) /
                            chartData.filter(d => d.fearGreed).length
                          )
                        : '-'}
                    </div>
                  </div>
                  <div className="text-center p-3 bg-secondary-custom rounded-lg">
                    <div className="text-caption text-secondary-custom mb-1">Avg News</div>
                    <div className="text-heading-3 font-semibold text-primary-custom">
                      {chartData.filter(d => d.newsSentiment).length > 0
                        ? Math.round(
                            chartData.reduce((sum, d) => sum + (d.newsSentiment || 0), 0) /
                            chartData.filter(d => d.newsSentiment).length
                          )
                        : '-'}
                    </div>
                  </div>
                  <div className="text-center p-3 bg-secondary-custom rounded-lg">
                    <div className="text-caption text-secondary-custom mb-1">Period</div>
                    <div className="text-heading-3 font-semibold text-primary-custom">30d</div>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-primary-custom">
            <button
              onClick={onClose}
              className="w-full sm:w-auto px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MarketSentimentChartModal;

